/**
 * Session Store
 */

import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { apiClient } from "../services/apiClient";

export const useSessionStore = defineStore("sessionStore", () => {

  const STORAGE_KEY = "session_api_base";

  const API_BASE = ref(
    import.meta.env.VITE_API_BASE ?? localStorage.getItem(STORAGE_KEY) ?? "http://localhost:8000"
  );

  const getApiBase = computed(() => API_BASE.value);

  function setApiBase(url) {
    const clean = url.trim().replace(/\/+$/, "");
    API_BASE.value = clean;
    localStorage.setItem(STORAGE_KEY, clean);
    loadSessions();
  }

  // -------------------------------------------------------------------------
  // LIDO / NÃO LIDO (VIEWED SESSIONS)
  // -------------------------------------------------------------------------
  const VIEWED_KEY = "viewed_sessions";
  const viewedSessions = ref(new Set(JSON.parse(localStorage.getItem(VIEWED_KEY) || "[]")));

  function markAsViewed(sessionId) {
    if (!viewedSessions.value.has(sessionId)) {
      viewedSessions.value.add(sessionId);
      localStorage.setItem(VIEWED_KEY, JSON.stringify([...viewedSessions.value]));
    }
  }

  function isViewed(sessionId) {
    return viewedSessions.value.has(sessionId);
  }

  // Format preference
  const sessionFormats = ref(new Map());

  function getSessionFormat(sessionId) {
    return sessionFormats.value.get(sessionId) ?? "hls";
  }

  function setSessionFormat(sessionId, format) {
    sessionFormats.value.set(sessionId, format);
  }

  function isFormatExplicit() { return false; } // kept for compat, unused

  function resetSessionFormat(sessionId) {
    sessionFormats.value.delete(sessionId);
  }

  function clearAllFormats() {
    sessionFormats.value.clear();
  }

  // Stats cache
  const sessionStats = ref(new Map());

  function updateSessionStats(sessionId, stats) {
    sessionStats.value.set(sessionId, { ...stats, cachedAt: Date.now() });
  }

  function getSessionStats(sessionId) {
    return sessionStats.value.get(sessionId) ?? null;
  }

  function clearSessionStats(sessionId) {
    sessionStats.value.delete(sessionId);
  }

  function clearAllStats() {
    sessionStats.value.clear();
  }

  // Main state
  const sessions       = ref([]);
  const sessionsTotal  = ref(0);
  const loading        = ref(false);
  const error          = ref(null);
  const currentSession = ref(null);

  async function loadSessions() {
    loading.value = true;
    error.value   = null;

    try {
      const response = await apiClient("/sessions");

      if (!Array.isArray(response?.data)) {
        throw new Error("Unexpected response format from /sessions");
      }

      sessions.value = response.data.map(_normalizeSession);
      sessionsTotal.value = response.total ?? sessions.value.length;

      // Keep currentSession in sync with fresh data
      if (currentSession.value) {
        const fresh = sessions.value.find((s) => s.id === currentSession.value.id);
        if (fresh) currentSession.value = fresh;
      }
    } catch (err) {
      console.error("[SessionStore] Failed to load sessions:", err);
      error.value = err.message;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Patches a single session in-memory without a full reload.
   * Mutates fields directly to avoid creating a new object reference,
   * which would trigger PlayerContainer's watch and reload the player.
   */
  function patchSession(sessionId, patch) {
    const session = sessions.value.find((s) => s.id === sessionId);
    if (!session) return;
    Object.assign(session, patch);
    // Do NOT reassign currentSession — mutations are reactive in place
  }

  /**
   * Patches a nested stage object (guac, hls, ocr, vlm, llm).
   * Mutates in place to avoid triggering player reload.
   */
  function patchSessionStage(sessionId, stage, patch) {
    const session = sessions.value.find((s) => s.id === sessionId);
    if (!session) return;
    if (session[stage]) {
      Object.assign(session[stage], patch);
    } else {
      session[stage] = { ...patch };
    }
  }

  function selectSession(session) {
    if (session?.id) {
      markAsViewed(session.id); // Registra como lido
      resetSessionFormat(session.id);
      // Set correct default format before PlayerContainer loads the session.
      // HLS is preferred when available; GUAC is the fallback.
      const hlsReady = session.hls?.status === "completed";
      setSessionFormat(session.id, hlsReady ? "hls" : "guac");
    }

    currentSession.value = session;

    window.dispatchEvent(
      new CustomEvent("session-selected", { detail: session })
    );

    console.log("[SessionStore] Session selected:", session?.id);
  }

  function clearSelection() {
    currentSession.value = null;
  }

  function _normalizeSession(raw) {
    return {
      ...raw,
      job_id: raw.id, // backwards compat alias
    };
  }

  return {
    API_BASE,
    getApiBase,
    setApiBase,

    sessionFormats,
    getSessionFormat,
    setSessionFormat,
    isFormatExplicit,
    resetSessionFormat,
    clearAllFormats,

    sessionStats,
    updateSessionStats,
    getSessionStats,
    clearSessionStats,
    clearAllStats,

    sessions,
    sessionsTotal,
    loading,
    error,
    currentSession,

    // Exportadas para uso nos componentes
    isViewed,
    markAsViewed,

    loadSessions,
    patchSession,
    patchSessionStage,
    selectSession,
    clearSelection,
  };
});